import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { GetTeamUsersResponse } from '@mai/platform-api/src/routes/team-users.schema';
import type { GetTeamByIdResponse } from '@mai/platform-api/src/routes/teams.schemas';

import { useTeamUsersByUserId } from '../queries/team-users';
import { useTeamsByIds } from '../queries/teams';
import { useSessionUser } from './SessionUserContext';

export interface AccessibleTeamsContextType {
  teams: GetTeamByIdResponse[];
  teamMemberships: GetTeamUsersResponse['items'];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  defaultTeam: GetTeamByIdResponse | null;
  hasTeams: boolean;
}

const AccessibleTeamsContext = createContext<
  AccessibleTeamsContextType | undefined
>(undefined);

export const AccessibleTeamsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { userId } = useSessionUser();

  // First, fetch the team memberships for the current user
  const teamMembershipsQuery = useTeamUsersByUserId(userId);

  // Extract team IDs from memberships
  const teamIds = useMemo(() => {
    if (!teamMembershipsQuery.data) return [];
    return teamMembershipsQuery.data.items.map(
      (membership) => membership.teamId,
    );
  }, [teamMembershipsQuery.data]);

  // Then fetch the actual team details
  const teamsQuery = useTeamsByIds(teamIds, {
    enabled: teamIds.length > 0,
  });

  // Find the default team
  const defaultTeam = useMemo(() => {
    if (!teamMembershipsQuery.data || !teamsQuery.data) return null;

    const defaultMembership = teamMembershipsQuery.data.items.find(
      (membership) => membership.isDefault,
    );
    if (!defaultMembership) return null;

    return (
      teamsQuery.data.items.find(
        (team) => team.teamId === defaultMembership.teamId,
      ) || null
    );
  }, [teamMembershipsQuery.data, teamsQuery.data]);

  // Compute loading and error states
  const isLoading = teamMembershipsQuery.isLoading || teamsQuery.isLoading;
  const isError = teamMembershipsQuery.isError || teamsQuery.isError;
  const error = teamMembershipsQuery.error || teamsQuery.error;

  const value: AccessibleTeamsContextType = {
    teams: teamsQuery.data?.items || [],
    teamMemberships: teamMembershipsQuery.data?.items || [],
    isLoading,
    isError,
    error: error as Error | null,
    defaultTeam,
    hasTeams: (teamsQuery.data?.items.length || 0) > 0,
  };

  return (
    <AccessibleTeamsContext.Provider value={value}>
      {children}
    </AccessibleTeamsContext.Provider>
  );
};

export const useAccessibleTeams = (): AccessibleTeamsContextType => {
  const context = useContext(AccessibleTeamsContext);
  if (context === undefined) {
    throw new Error(
      'useAccessibleTeams must be used within an AccessibleTeamsProvider',
    );
  }
  return context;
};
